<template>
  <div class="bank-entries-container card-item secondary">
    <h5 class="is-size-5 pb-3">{{ $t('transactions.bankEntriesTitle') }}</h5>
    <div id="search" class="control entry-searcher has-icons-left">
      <b-taginput
        id="search-bank-entries-input"
        v-model="search"
        icon="search"
        rounded
        ellipsis
        :placeholder="$t('generalWords.Search')"
        size="is-small"
        close-type="is-success"
        class="search-tags" />
    </div>
    <div v-if="totals" class="validation-totals my-2">
      <b-tag rounded class="has-text-weight-bold">
        <span>Total: </span>
        <span>{{ formatNumberByCountry(selectedEntries.totalAmount) }}</span>
      </b-tag>
      <b-tag rounded class="has-text-weight-bold" :type="selectedEntries.totalAmount - currentTransactionAmount < 0 ? 'is-danger is-light' : 'is-primary is-light'">
        <span>{{ $t('generalWords.Difference') }}:</span>
        <span>
          {{ formatNumberByCountry(selectedEntries.totalAmount - currentTransactionAmount) }}
        </span>
      </b-tag>
    </div>
    <section class="transfers-table">
      <div v-for="entry in filteredBankEntries" :key="entry._id" class="bank-entries p-2 is-flex is-justify-content-flex-start">
        <input
          :id="`${idPrefix}-${entry._id}`"
          v-model="checkedEntries"
          class="mr-5"
          :type="type"
          :value="type === 'radio' ? [entry] : entry"
          @change="$emit('input', selectedEntries)">
        <div class="is-flex bank-entries-rows">
          <label :for="`${idPrefix}-${entry._id}`" class="item-amount">{{ formatNumberByCountry(entry.amount) }}</label>
          <label :for="`${idPrefix}-${entry._id}`" class="item-name entity-name">{{ formatName(entry.entity.name) }}</label>
          <label :for="`${idPrefix}-${entry._id}`" class="item-date">{{ formatDate(entry.date, 'DD/MM/YYYY') }}</label>
          <label :for="`${idPrefix}-${entry._id}`" class="item-idnumber">{{ formatRut(entry.entity.idNumber || '') }}</label>
          <label :for="`${idPrefix}-${entry._id}`" class="item-bankname">{{ formatName(entry.entity.bankName) }}</label>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { dateToFormatedString } from '@/infrastructure/date';
import { formatNumber, formatNumberByCountry, formatName } from '@/infrastructure/format';

export default {
  props: {
    bankEntries: { type: Array, default: () => [] },
    type: { type: String, default: 'checkbox' },
    idPrefix: { type: String, default: '' },
    defaultSearch: { type: Array, default: () => [] },
    currentTransactionAmount: { type: Number, default: 0 },
    totals: { type: Boolean, default: true }
  },
  data() {
    return {
      checkedEntries: [],
      search: this.defaultSearch
    };
  },
  computed: {
    filteredBankEntries() {
      if (this.search.length === 0) {
        return this.bankEntries;
      }
      const searchFilterValue = entryValue => this.search.some(searchValue => entryValue.includes(this.parseString(searchValue)));
      const filterResults = this.bankEntries.filter(entry => {
        const bools = [];
        bools.push(searchFilterValue(this.formatDate(entry.date)));
        bools.push(searchFilterValue(this.parseString(entry.entity.name)));
        bools.push(searchFilterValue(this.parseString(entry.entity.idNumber)));
        bools.push(searchFilterValue(this.parseString(entry.entity.bankName)));
        bools.push(searchFilterValue(this.parseString(entry.amount)));
        return bools.some(e => e);
      });
      if (this.checkedEntries.length) {
        this.checkedEntries.forEach(checkedEntry => {
          const isInfilterResults = filterResults.some(result => result._id === checkedEntry._id);
          if (!isInfilterResults) {
            filterResults.push(checkedEntry);
          }
        });
      }
      return filterResults;
    },
    selectedEntries() {
      const template = { totalAmount: 0, bankEntriesIds: [], bankEntriesEntities: [] };
      if (!this.checkedEntries.length) return template;
      return this.checkedEntries.reduce((acumIn, entry) => {
        const acum = JSON.parse(JSON.stringify(acumIn));
        acum.totalAmount += entry.amount;
        acum.bankEntriesIds.push(entry._id);
        acum.bankEntriesEntities.push(entry.entity);
        return acum;
      }, template);
    }
  },
  methods: {
    formatNumberByCountry(number) {
      const fractionDigits = {
        br: 2,
        cl: 0,
        pe: 2
      }[this.$i18n.locale];
      return formatNumberByCountry({ number, countryIn: this.$i18n.locale, usePrefix: true, fractionDigits, currencyType: 'secondary' });
    },
    formatRut(rut) {
      if (!rut) {
        return '';
      }
      const rutParts = rut.split('-');
      return `${formatNumber({ number: Number(rutParts[0]) })}-${rutParts[1]}`;
    },
    formatName(name) {
      return formatName(name);
    },
    formatDate: (date, format = 'DD/MM/YYYY', inputFormat = '') => dateToFormatedString(date, format, inputFormat),
    parseString: string => {
      if (!string) return '';
      return string.toString().toUpperCase().replace(/\./g, '');
    }
  }
};
</script>

<style scoped>
  .bank-entries-container {
    padding: 15px;
    overflow: auto;
    font-size: 0.65rem;
    container-type: inline-size;
  }
  .transfers-table {
    overflow: hidden;
  }
  .item-amount {
    width: 6rem;
  }
  .item-name {
    width: 11rem;
  }
  .item-date {
    width: 5rem;
  }
  .item-idnumber {
    width: 5rem;
  }
  .item-bankname {
    width: 6rem;
  }
  .bank-entries .entity-name {
    text-transform: capitalize;
  }
  .entry-searcher {
    margin-bottom: 15px;
  }
  .bank-entries:nth-child(even) {
    background-color: #f2f2f2;
  }
  .validation-totals {
    display: flex;
    flex-flow: row wrap;
    align-items: left;
    justify-content: left;
    gap: 0.5rem 2rem;
    font-size: 1rem;
  }
  .validation-totals p {
    display:flex;
    gap: 0.5rem;
  }
  .validation-totals p span.surplus {
    color: var(--primary);
  }
  .validation-totals p span.deficit {
    color: var(--secondary) !important;
  }
  .validation-totals p span.insu
  .ul__li-circle {
    margin-left: 17px;
    list-style-type: initial;
  }
  .search-tags {
    width: 100%;
  }
  @container (max-width: 590px) {
    .bank-entries-rows {
      flex-direction: column
    }
  }
</style>
